import { inject, provide, ref, computed } from 'vue';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useRouter } from 'vue-router';
import { useWindowSize } from '@vueuse/core';

const SidebarControl = Symbol('SidebarControl');

// custom: the sidebar is no longer user-resizable (task #6). It keeps a single
// standard width that only adapts to the viewport — narrower on smaller/squarer
// monitors, but never below a width where the labels still fit. It never
// collapses to an icon-only rail, so menu text is always visible.
//
// custom (2026-08-07): 272 → 236. A largura anterior foi calculada para uma linha de menu
// de 42px com rótulo de 15px; a escala inteira desceu para 32px/13px (padrão Attio), e uma
// barra larga em volta de texto pequeno não lê como espaçosa, lê como vazia.
// 236 = 220 de conteúdo + 8 de respiro de cada lado, e 220 ainda comporta o rótulo mais
// longo da navegação sem reticências.
// 236 → 252 (2026-08-08): eu tinha apertado demais. Com a barra em 236 e o conteúdo em
// largura cheia, a área de gráfico ficava desproporcional — "tá muito grande a tela dos
// gráficos", e o desequilíbrio entre as duas colunas é que ficava esquisito, não o gráfico.
// 252 -> 272 -> 296 (2026-08-09). O numero saiu de MEDIR a referencia na MESMA largura de
// janela, que e o unico jeito honesto de comparar: na janela de 1206px do print que o Gustavo
// trouxe, a barra da Basepoint tem 296px e a nossa tinha 232. Sessenta e quatro pixels a menos
// na mesma tela - a queixa dele ("a tela do lado esta muito grande em relacao a sidebar") era
// medida, nao impressao.
//
// O degrau compacto sobe junto (232 -> 272): ele existe para telas menores, mas 232 era
// estreito ate para elas - e era justamente na faixa de 1206px que a diferenca para a
// referencia aparecia inteira.
//
// 296 -> 312 e 272 -> 288 (2026-08-11). Pedido do Gustavo NA MESMA FRASE em que ele mandou
// DIMINUIR a fonte do menu ("tá muito grande, tá amador"), e os dois movimentos são um só:
// o que fazia a barra parecer apertada não era a largura, era a letra de 17px dentro dela.
// Corpo menor + coluna mais larga é a razão que Attio/Linear usam — a folga fica em volta do
// texto, não dentro dele. O passo é de 16px (um degrau da grade de 8), porque largura demais
// em volta de texto pequeno deixa de ler como espaçosa e passa a ler como vazia; foi
// exatamente o erro cometido na direção oposta em 2026-08-07 (ver o parágrafo do 236).
const SIDEBAR_WIDTH_STANDARD = 312; // brand shell width on roomy displays
const SIDEBAR_WIDTH_COMPACT = 288; // smaller/squarer monitors — labels still fit
const COMPACT_BREAKPOINT = 1280; // below this viewport width, use the compact size

// Shared state for active popover (only one can be open at a time)
const activePopover = ref(null);
let globalCloseTimeout = null;

// ⚠️ OCULTAR A SIDEBAR (2026-08-09) — e isto NAO e o `isCollapsed` logo abaixo.
//
// Sao dois conceitos diferentes e confundi-los seria facil:
//   `isCollapsed` — o trilho so-de-icones do upstream. Segue DESLIGADO para sempre (ver a
//                   nota original: o texto do menu esta sempre visivel nesta plataforma).
//   `sidebarOculta` — a barra SOME inteira e sobra um botao flutuante para traze-la de
//                   volta. Foi o que o Gustavo pediu, com a referencia na mao: "ele oculta e
//                   abre a sidebar".
//
// O estado e MODULO, nao componente: o botao de reabrir vive fora da barra (quando ela nao
// esta na tela, nao ha barra para hospeda-lo), entao os dois precisam ler o mesmo ref.
//
// Persiste em localStorage porque e preferencia de espaco de trabalho: quem escondeu a barra
// para ver o board inteiro nao quer refaze-lo a cada F5. `try` porque em aba anonima o acesso
// pode estourar, e esconder a barra nao pode derrubar a aplicacao.
const LS_SIDEBAR_OCULTA = 'cw_custom:sidebar_oculta';

const lerPreferencia = () => {
  try {
    return localStorage.getItem(LS_SIDEBAR_OCULTA) === '1';
  } catch {
    return false;
  }
};

const sidebarOculta = ref(lerPreferencia());

export function useSidebarOculta() {
  const alternar = () => {
    sidebarOculta.value = !sidebarOculta.value;
    try {
      localStorage.setItem(LS_SIDEBAR_OCULTA, sidebarOculta.value ? '1' : '0');
    } catch {
      // Sem persistencia a barra volta no proximo carregamento. Degradacao aceitavel.
    }
  };

  return { sidebarOculta, alternar };
}

export function useSidebarResize() {
  const { width: windowWidth } = useWindowSize();

  // Standard width, responsive to the viewport. No drag handle, no persistence.
  const sidebarWidth = computed(() =>
    windowWidth.value < COMPACT_BREAKPOINT
      ? SIDEBAR_WIDTH_COMPACT
      : SIDEBAR_WIDTH_STANDARD
  );

  // Text is always visible: the sidebar never collapses to an icon rail.
  const isCollapsed = computed(() => false);

  return {
    sidebarWidth,
    isCollapsed,
  };
}

export function usePopoverState() {
  const setActivePopover = name => {
    clearTimeout(globalCloseTimeout);
    activePopover.value = name;
  };

  const closeActivePopover = () => {
    activePopover.value = null;
  };

  const scheduleClose = (delay = 150) => {
    clearTimeout(globalCloseTimeout);
    globalCloseTimeout = setTimeout(() => {
      closeActivePopover();
    }, delay);
  };

  const cancelClose = () => {
    clearTimeout(globalCloseTimeout);
  };

  return {
    activePopover,
    setActivePopover,
    closeActivePopover,
    scheduleClose,
    cancelClose,
  };
}

export function useSidebarContext() {
  const context = inject(SidebarControl, null);
  if (context === null) {
    throw new Error(`Component is missing a parent <Sidebar /> component.`);
  }

  const router = useRouter();
  const { shouldShow } = usePolicy();

  const resolvePath = to => {
    if (to) return router.resolve(to)?.path || '/';
    return '/';
  };

  // Helper to find route definition by name without resolving
  const findRouteByName = name => {
    const routes = router.getRoutes();
    return routes.find(route => route.name === name);
  };

  const resolvePermissions = to => {
    if (!to) return [];

    // If navigationPath param exists, get the target route definition
    if (to.params?.navigationPath) {
      const targetRoute = findRouteByName(to.params.navigationPath);
      return targetRoute?.meta?.permissions ?? [];
    }

    return router.resolve(to)?.meta?.permissions ?? [];
  };

  const resolveFeatureFlag = to => {
    if (!to) return '';

    // If navigationPath param exists, get the target route definition
    if (to.params?.navigationPath) {
      const targetRoute = findRouteByName(to.params.navigationPath);
      return targetRoute?.meta?.featureFlag || '';
    }

    return router.resolve(to)?.meta?.featureFlag || '';
  };

  const resolveInstallationType = to => {
    if (!to) return [];

    // If navigationPath param exists, get the target route definition
    if (to.params?.navigationPath) {
      const targetRoute = findRouteByName(to.params.navigationPath);
      return targetRoute?.meta?.installationTypes || [];
    }

    return router.resolve(to)?.meta?.installationTypes || [];
  };

  const isAllowed = to => {
    const permissions = resolvePermissions(to);
    const featureFlag = resolveFeatureFlag(to);
    const installationType = resolveInstallationType(to);

    return shouldShow(featureFlag, permissions, installationType);
  };

  return {
    ...context,
    resolvePath,
    resolvePermissions,
    resolveFeatureFlag,
    isAllowed,
  };
}

export function provideSidebarContext(context) {
  provide(SidebarControl, context);
}
